"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

const RoleContext = createContext();

export const useRoles = () => useContext(RoleContext);

// CHANGED: Added a safe JSON parsing helper to prevent "Unexpected token '<'" crashes.
// This guards against cases where the backend returns an HTML error page (404/500/proxy errors)
// or an empty body instead of valid JSON. Used in place of both bare `res.json()` and
// `res.json().catch(() => null)` for consistent, predictable fallback shape.
const safeParseJSON = async (res) => {
  try {
    const text = await res.text();
    if (!text) {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (parseErr) {
      return null;
    }
  } catch (readErr) {
    return null;
  }
};

export const RoleProvider = ({ children }) => {
  const [roles, setRoles] = useState([]);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  // ---------------------------
  // 🔹 Get All Roles — fetch operation, no toasts
  // CHANGED: Removed toast.error calls — this is a GET/fetch operation, not a user action.
  // ---------------------------
  const fetchRoles = async () => {
    try {
      const res = await fetch(`${BASE_URL}/roles`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (res.ok) {
        setRoles(data?.data || []);
      } else {
        console.warn("⚠️ Failed to fetch roles:", data); // CHANGED: replaced toast.error with console.warn
      }
    } catch (error) {
      console.error("❌ Error fetching roles:", error);
      // CHANGED: removed toast.error("Error fetching roles")
    }
  };

  // ---------------------------
  // 🔹 Create Role — user action, toasts preserved
  // ---------------------------
  const createRole = async (roleData) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const auditId = user?.id || null;

      const payload = {
        ...roleData,
        create_audit_id: auditId,
      };

      const res = await fetch(`${BASE_URL}/roles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json().catch(() => null)

      if (res.ok) {
        fetchRoles();
        toast.success(responseData?.message || "Role created successfully"); // KEPT: user-initiated action
      } else {
        console.warn("⚠️ Failed to create role:", responseData);
        toast.error(responseData?.message || "Failed to create role"); // KEPT: user-initiated action
      }

      return res.ok;
    } catch (error) {
      console.error("❌ Error creating role:", error);
      toast.error("Error creating role"); // KEPT: user-initiated action
    }
  };

  // ---------------------------
  // 🔹 Update Entire Role (name, status, permissions) — user action, toasts preserved
  // ---------------------------
  const updateRole = async (id, updatedData) => {
    try {
      // Get user/audit info from localStorage
      const user = JSON.parse(localStorage.getItem("user"));
      const auditId = user?.id || null;

      // Merge audit ID into payload
      const payload = {
        ...updatedData,
        update_audit_id: auditId,
      };

      const res = await fetch(`${BASE_URL}/roles/${id}`, {
        method: "PUT", // full update
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const responseData = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json().catch(() => null)

      if (res.ok) {
        fetchRoles();
        toast.success(responseData?.message || "Role updated successfully"); // KEPT: user-initiated action
      } else {
        console.warn("⚠️ Failed to update role:", responseData);
        toast.error(responseData?.message || "Failed to update role"); // KEPT: user-initiated action
      }

      return res.ok;
    } catch (error) {
      console.error("❌ Error updating role:", error);
      toast.error("Error updating role"); // KEPT: user-initiated action
    }
  };

  // ---------------------------
  // 🔹 Update Only Status — user action, toasts preserved
  // ---------------------------
  const updateStatus = async (id, status) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const auditId = user?.id || null;

      const payload = {
        status,
        update_audit_id: auditId,
      };

      const res = await fetch(`${BASE_URL}/roles/${id}/status`, {
        method: "POST", // Or PATCH if your API expects PATCH
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json().catch(() => null)

      if (res.ok) {
        fetchRoles();
        toast.success(responseData?.message || "Role status updated successfully"); // KEPT: user-initiated action
      } else {
        console.warn("⚠️ Failed to update status:", responseData);
        toast.error(responseData?.message || "Failed to update status"); // KEPT: user-initiated action
      }

      return res.ok;
    } catch (error) {
      console.error("❌ Error updating status:", error);
      toast.error("Error updating status"); // KEPT: user-initiated action
    }
  };

  // ---------------------------
  // 🔹 Update Only Permissions — user action, toasts preserved
  // ---------------------------
  const updatePermissions = async (id, permissions) => {
    try {
      const user = JSON.parse(localStorage.getItem("user")); // get logged-in user
      const update_audit_id = user?.id;
      const res = await fetch(`${BASE_URL}/roles/${id}/permissions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ permissions, update_audit_id }),
      });

      if (res.ok) {
        const responseData = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json().catch(() => null)
        await fetchRoles();
        toast.success(responseData?.message || "Permissions updated successfully"); // KEPT: user-initiated action
        return true;
      } else {
        const errorData = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json().catch(() => null)
        console.warn("⚠️ Failed to update permissions:", errorData);
        toast.error(errorData?.message || "Failed to update permissions"); // KEPT: user-initiated action
        return false;
      }
    } catch (error) {
      console.error("❌ Error updating permissions:", error);
      toast.error("Error updating permissions"); // KEPT: user-initiated action
      return false;
    }
  };

  // ---------------------------
  // 🔹 Delete Role — user action, toasts preserved
  // ---------------------------
  const deleteRole = async (id) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const auditId = user?.id || null;
      const res = await fetch(`${BASE_URL}/roles/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id: auditId }),
      });

      const responseData = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json().catch(() => null)

      if (res.ok) {
        fetchRoles();
        toast.success(responseData?.message || "Role deleted successfully"); // KEPT: user-initiated action
      } else {
        console.warn("⚠️ Failed to delete role:", responseData);
        toast.error(responseData?.message || "Failed to delete role"); // KEPT: user-initiated action
      }

      return res.ok;
    } catch (error) {
      console.error("❌ Error deleting role:", error);
      toast.error("Error deleting role"); // KEPT: user-initiated action
    }
  };

  // ---------------------------
  // 🔹 Get Action Permissions by Role ID or name — fetch operation, no toasts
  // CHANGED: Removed toast.error calls — this is a GET/fetch operation, not a user action.
  // ---------------------------
  const getPermissionsByRole = async (identifier) => {
    try {
      const res = await fetch(`${BASE_URL}/roleactionpermission/${identifier}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json().catch(() => null)

      if (res.ok && data?.permissions) {
        return data.permissions; // { Employees: ["view", "edit"] }
      } else {
        console.warn("⚠️ Failed to fetch action permissions:", data); // CHANGED: replaced toast.error with console.warn
        return {};
      }
    } catch (err) {
      console.error("❌ Error fetching action permissions:", err);
      // CHANGED: removed toast.error("Error fetching action permissions")
      return {};
    }
  };

  // ---------------------------
  // 🔹 Save Action Permissions — user action, toasts preserved
  // ---------------------------
  const saveRolePermissions = async (identifier, permissions) => {
    try {
      const user =
        typeof window !== "undefined"
          ? JSON.parse(localStorage.getItem("user") || "null")
          : null;

      const update_audit_id = user?.id ?? user?.user_id ?? 1;

      const payload = {
        update_audit_id,
        action_permissions: permissions,
      };

      const res = await fetch(`${BASE_URL}/roleactionpermission/${identifier}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: JSON.stringify(payload),
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json().catch(() => null)

      if (res.ok) {
        await fetchRoles();
        toast.success(data?.message || "Action permissions saved successfully"); // KEPT: user-initiated action
        return { success: true };
      } else {
        console.warn("⚠️ Failed to save action permissions", data);
        toast.error(data?.message || "Failed to save action permissions"); // KEPT: user-initiated action
        return { success: false, error: data };
      }
    } catch (err) {
      console.error("❌ Error saving action permissions:", err);
      toast.error("Error saving action permissions"); // KEPT: user-initiated action
      return { success: false, error: err };
    }
  };

  // Auto fetch on start
  useEffect(() => {
    if (token) fetchRoles();
  }, [token]);

  return (
    <RoleContext.Provider
      value={{
        roles,
        fetchRoles,
        createRole,
        updateRole,
        updateStatus,
        updatePermissions,
        deleteRole,
        getPermissionsByRole,
        saveRolePermissions,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
};